import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check, Circle } from "lucide-react";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";

/**
 * Searchable button-catalog picker. Replaces the old 4-item Horn/MOP/Metal dropdown.
 * Value is stored as the full catalog row (so we keep code, color, price) — falls
 * back to a plain string if the parent only passes a label.
 *
 * Usage:
 *   <ButtonStyleSelect
 *     label="Button Style"
 *     category="suit"
 *     value={jacketStyling.button_style}
 *     onChange={(val) => setJacketStyling({ ...jacketStyling, button_style: val })}
 *   />
 *
 * `value` may be a string (legacy: "Horn") or an object ({code, name, color, ...}).
 */
export default function ButtonStyleSelect({
  label = "Button Style",
  category = "suit",          // "suit" | "overcoat" | "shirt"
  value,
  onChange,
  testId = "button-style-select",
}) {
  const { catalog } = useFabricCatalog();
  const buttons = catalog?.rc?.buttons?.[category] || [];

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Normalise current value → label + code for the trigger.
  // value may be a plain string (legacy: "Horn") or "Name (CODE)" produced by this picker.
  const m = typeof value === "string" ? value.match(/^(.*?)\s*\(([^)]+)\)\s*$/) : null;
  const currentLabel = m ? m[1] : (value || "—");
  const currentCode = m ? m[2] : null;
  // Find the catalog row matching the current code so we can render the color dot
  const currentBtn = currentCode ? buttons.find((b) => b.code === currentCode) : null;

  // Group + filter
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return buttons;
    return buttons.filter((b) =>
      [b.code, b.name, b.color, b.material, b.sizes]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(needle))
    );
  }, [buttons, q]);

  // Group by material for clarity
  const grouped = useMemo(() => {
    const m = new Map();
    for (const b of filtered) {
      const k = b.material || "Other";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(b);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const pick = (btn) => {
    // Emit a string "Name (CODE)" — preserves backward-compat with downstream
    // string consumers (Submit Summary, AI prompt builder) while keeping the code visible.
    onChange?.(`${btn.name} (${btn.code})`);
    setOpen(false);
    setQ("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {label ? <label className="text-xs text-slate-400 block mb-1">{label}</label> : null}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full bg-[#0a1628] border border-amber-500/40 rounded-md h-11 px-3 text-sm text-left flex items-center gap-2 hover:border-amber-500/70 transition-colors"
        data-testid={`${testId}-trigger`}
      >
        <Circle
          size={12}
          fill={currentBtn?.color ? currentBtn.color.toLowerCase() : "transparent"}
          className="text-slate-500 shrink-0"
        />
        {currentCode && (
          <span className="font-mono font-semibold text-sm text-amber-400 shrink-0">
            {currentCode}
          </span>
        )}
        <span className="flex-1 truncate text-slate-300 text-sm">
          {currentBtn ? (
            <>
              {currentBtn.color ? `${currentBtn.color} · ` : ""}
              {currentBtn.material || ""}
            </>
          ) : (
            <span className="text-slate-500">— Select button —</span>
          )}
        </span>
        {value && (
          <span
            onClick={clear}
            role="button"
            tabIndex={-1}
            className="text-slate-500 hover:text-red-400 cursor-pointer shrink-0"
            data-testid={`${testId}-clear`}
          >
            <X size={12} />
          </span>
        )}
        <Search size={14} className="text-slate-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#0a1628] border border-white/10 rounded-md shadow-xl max-h-[28rem] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-white/5 flex items-center gap-2">
            <Search size={14} className="text-slate-500 shrink-0" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${buttons.length} ${category} buttons…`}
              className="bg-transparent text-sm outline-none flex-1 placeholder-slate-600"
              data-testid={`${testId}-search`}
            />
            {q && (
              <button onClick={() => setQ("")} className="text-slate-500 hover:text-white">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {grouped.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500">No buttons match "{q}"</div>
            )}
            {grouped.map(([material, items]) => (
              <div key={material} className="py-1">
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-white/[0.03] sticky top-0">
                  {material} <span className="text-slate-600 font-normal">· {items.length}</span>
                </div>
                {items.map((b) => {
                  const selected = currentCode === b.code;
                  return (
                    <button
                      key={b.code}
                      type="button"
                      onClick={() => pick(b)}
                      className={`w-full px-3 py-2 text-left border-b border-white/5 last:border-0 transition-colors ${
                        selected
                          ? "bg-amber-500/10"
                          : "hover:bg-white/5"
                      }`}
                      data-testid={`${testId}-opt-${b.code}`}
                    >
                      {/* Top row: color dot · name · code badge · price · selected check */}
                      <div className="flex items-center gap-2 text-xs">
                        <Circle
                          size={12}
                          fill={b.color ? b.color.toLowerCase() : "transparent"}
                          className="text-slate-600 shrink-0"
                        />
                        <span className={`flex-1 truncate ${selected ? "text-amber-300 font-medium" : "text-slate-100"}`}>
                          {b.name}
                        </span>
                        <code className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded font-mono shrink-0">
                          {b.code}
                        </code>
                        {b.price ? (
                          <span className="text-[10px] text-emerald-400 font-mono shrink-0">${b.price}</span>
                        ) : null}
                        {selected && <Check size={12} className="text-amber-400 shrink-0" />}
                      </div>
                      {/* Bottom row: color · material · sizes */}
                      <div className="mt-1 text-[10px] text-slate-400 truncate">
                        {b.color && <span>{b.color}</span>}
                        {b.material && <> · <span className="text-slate-500">{b.material}</span></>}
                        {b.sizes && <> · <span className="text-slate-500">{b.sizes}</span></>}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="px-3 py-1.5 border-t border-white/5 text-[10px] text-slate-500 flex justify-between bg-white/[0.02]">
            <span>{filtered.length} of {buttons.length}</span>
            <button
              onClick={() => {
                onChange?.("");
                setOpen(false);
              }}
              className="hover:text-red-400"
              data-testid={`${testId}-none`}
            >
              Clear selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
